import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import i18n from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/errors";
import { AuthLayout } from "@/pages/auth/AuthLayout";
import { Field } from "@/components/common/FormField";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { GoogleSoonButton } from "@/pages/auth/GoogleSoonButton";

const strongEnough = (pwd) => pwd.length >= 8 && /[A-Za-z]/.test(pwd) && /\d/.test(pwd);

export default function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const errs = {};
    if (!strongEnough(form.password)) errs.password = t("auth.passwordRules");
    if (form.password !== form.confirm) errs.confirm = t("auth.passwordMismatch");
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await register({ email: form.email.trim(), password: form.password, full_name: form.full_name.trim(), locale: i18n.language || "fr-CH" });
      navigate("/onboarding", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title={t("auth.registerTitle")}
      subtitle={t("auth.registerSubtitle")}
      footer={
        <>
          {t("auth.hasAccount")}{" "}
          <Link to="/login" className="og-focus font-medium text-primary hover:underline" data-testid="register-login-link">{t("auth.login")}</Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate data-testid="register-form">
        {error && <Alert className="border-rose-200 bg-rose-50 text-rose-900" data-testid="register-error"><AlertDescription>{error}</AlertDescription></Alert>}
        <Field id="register-name" label={t("auth.fullName")}>
          <Input id="register-name" autoComplete="name" required minLength={2} value={form.full_name} onChange={set("full_name")} placeholder={t("auth.namePlaceholder")} data-testid="register-name-input" />
        </Field>
        <Field id="register-email" label={t("auth.email")}>
          <Input id="register-email" type="email" autoComplete="email" required value={form.email} onChange={set("email")} placeholder={t("auth.emailPlaceholder")} data-testid="register-email-input" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="register-password" label={t("auth.password")} hint={t("auth.passwordRules")} error={fieldErrors.password}>
            <Input id="register-password" type="password" autoComplete="new-password" required value={form.password} onChange={set("password")} data-testid="register-password-input" aria-invalid={!!fieldErrors.password} />
          </Field>
          <Field id="register-confirm" label={t("auth.passwordConfirm")} error={fieldErrors.confirm}>
            <Input id="register-confirm" type="password" autoComplete="new-password" required value={form.confirm} onChange={set("confirm")} data-testid="register-password-confirm-input" aria-invalid={!!fieldErrors.confirm} />
          </Field>
        </div>
        <Button type="submit" className="h-10 w-full" disabled={busy || !form.email || !form.password || !form.full_name} data-testid="register-submit-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {t("auth.register")}
        </Button>
        <p className="text-center text-xs text-muted-foreground">{t("auth.registerNext")}</p>
        <GoogleSoonButton />
      </form>
    </AuthLayout>
  );
}
